import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, CARD_DESCRIPTOR } from '../data/mock';
import { HelpCircle, ChevronDown, Cpu, Mail, Phone } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    document.title = 'FAQ | Horizon Techline';
  }, []);

  const faqs = [
    {
      category: 'Orders & Shipping',
      questions: [
        {
          q: 'How long does shipping take?',
          a: 'Your order is processed and shipped via USPS within 1 business day. Delivery usually lands within 2-4 business days within the United States.'
        },
        {
          q: 'Do you offer free shipping?',
          a: 'Yes! Every order ships with free USPS Standard Shipping within the United States \u2014 no minimum purchase required.'
        },
        {
          q: 'How can I track my order?',
          a: 'Once your order ships, you\'ll receive an email with a tracking number. You can use this number to track your package on the USPS website.'
        },
        {
          q: 'Do you ship internationally?',
          a: 'Currently, we only ship within the United States. We hope to expand to international shipping in the future.'
        }
      ]
    },
    {
      category: 'Returns & Refunds',
      questions: [
        {
          q: 'What is your return policy?',
          a: 'We offer a 30-day return policy for unused items in original packaging. Simply contact us to initiate a return.'
        },
        {
          q: 'How long do refunds take?',
          a: 'Refunds are processed within 5-7 business days after we receive the returned item. The refund will appear on your original payment method.'
        },
        {
          q: 'Can I exchange an item?',
          a: 'Yes! Contact our support team to arrange an exchange. We\'ll help you find the right product for your needs.'
        }
      ]
    },
    {
      category: 'Products',
      questions: [
        {
          q: 'Are your products high quality?',
          a: 'Absolutely! We carefully test and select every product in our catalog to ensure it meets our high standards for quality and performance.'
        },
        {
          q: 'Do you offer product warranties?',
          a: 'Many of our products come with manufacturer warranties. Contact us for specific warranty information on any product.'
        },
        {
          q: 'Can I get product recommendations?',
          a: 'Yes! Our team is happy to help you find the right gadget or accessory for your setup. Contact us with your questions.'
        }
      ]
    },
    {
      category: 'Payment & Security',
      questions: [
        {
          q: 'What payment methods do you accept?',
          a: 'We accept all major credit cards (Visa, Mastercard, American Express) and other secure payment methods at checkout.'
        },
        {
          q: 'Is my payment information secure?',
          a: 'Yes! We use SSL encryption and secure payment processing to protect your information. We never store your full credit card details.'
        },
        {
          q: 'How will the charge appear on my statement?',
          a: `Charges will appear as "${CARD_DESCRIPTOR}" on your credit card statement.`
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="">
        {/* Banner Section */}
        <section className="relative bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-50 border border-athletic-100 rounded-full text-athletic-700 text-xs font-semibold mb-4">
              <HelpCircle className="w-3.5 h-3.5" />
              HELP CENTER
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-gray-900 font-extrabold mb-3">
              Frequently Asked Questions
            </h1>
            <p className="text-gray-600 max-w-lg">
              Find answers to common questions about our products and services
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* FAQ Categories */}
          <div className="space-y-10">
            {faqs.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <h2 className="font-display text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-athletic-700" />
                  {category.category}
                </h2>
                <div className="space-y-3">
                  {category.questions.map((faq, questionIndex) => {
                    const key = `${categoryIndex}-${questionIndex}`;
                    const isOpen = openIndex === key;
                    return (
                      <div 
                        key={questionIndex}
                        className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden"
                      >
                        <button
                          onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-100/50 transition-colors"
                        >
                          <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                          <ChevronDown className={`w-5 h-5 text-athletic-700 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-5">
                            <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div className="mt-16 bg-gradient-to-br from-athletic-500/10 to-athletic-600/10 border border-athletic-100 rounded-2xl p-8 text-center">
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-gray-600 mb-6">
              Our support team is here to help. Reach out and we'll get back to you as soon as possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-athletic-500 text-white font-semibold rounded-xl hover:bg-athletic-400 transition-colors"
              >
                <Mail className="w-5 h-5" />
                {CONTACT_INFO.email}
              </a>
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-900 font-semibold rounded-xl hover:bg-gray-200 transition-colors border border-gray-200"
              >
                <Phone className="w-5 h-5" />
                {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default FAQPage;
